import { useState } from "react";
import { useAuth } from "../auth/AuthContext";
import { ModelMode, ModelModeConfig } from "../config/modelConfig";
import { enrollSecurityKey, changePassword } from "../auth/kratosSettings";

interface SettingsPanelProps {
  open: boolean;
  onClose: () => void;
  modelMode: ModelMode;
  onModelModeChange: (mode: ModelMode) => void;
  modelConfig: Record<ModelMode, ModelModeConfig>;
  fallbackNotice: string | null;
  onOpenAdmin?: () => void;
}

export function SettingsPanel({
  open,
  onClose,
  modelMode,
  onModelModeChange,
  modelConfig,
  fallbackNotice,
  onOpenAdmin,
}: SettingsPanelProps) {
  const { user, logout } = useAuth();
  const [signingOut, setSigningOut] = useState(false);
  const [enrollingKey, setEnrollingKey] = useState(false);
  const [keyError, setKeyError] = useState<string | null>(null);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [changingPassword, setChangingPassword] = useState(false);

  if (!open) return null;

  const handleSignOut = async () => {
    setSigningOut(true);
    try {
      await logout();
    } finally {
      setSigningOut(false);
    }
  };

  const handleEnrollSecurityKey = async () => {
    setEnrollingKey(true);
    setKeyError(null);
    try {
      const result = await enrollSecurityKey(`Security Key ${new Date().toLocaleDateString()}`);
      if (result.kind === "error") {
        setKeyError(result.message);
      } else if (result.kind === "enrolled") {
        setKeyError(null);
        onClose();
      }
    } catch (err) {
      setKeyError(err instanceof Error ? err.message : "Failed to enroll security key");
    } finally {
      setEnrollingKey(false);
    }
  };

  const handleChangePassword = async () => {
    setPasswordError(null);
    if (!newPassword) {
      setPasswordError("Enter a new password");
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError("Passwords don't match");
      return;
    }
    if (newPassword.length < 8) {
      setPasswordError("Password must be at least 8 characters");
      return;
    }

    setChangingPassword(true);
    try {
      const result = await changePassword(newPassword);
      if (result.kind === "error") {
        setPasswordError(result.message);
      } else {
        setShowPasswordChange(false);
        setNewPassword("");
        setConfirmPassword("");
      }
    } catch (err) {
      setPasswordError(err instanceof Error ? err.message : "Failed to change password");
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-start p-4 sm:items-center sm:p-6">
      <button
        type="button"
        className="absolute inset-0 bg-black/40"
        aria-label="Close settings"
        onClick={onClose}
      />
      <div className="relative z-10 w-full max-w-md rounded-2xl border border-gray-200 bg-white p-5 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-bold text-brand-navy">Settings</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            ✕
          </button>
        </div>

        <p className="mb-3 text-sm font-medium text-gray-700">Model mode</p>
        <div className="space-y-2">
          {(Object.entries(modelConfig) as [ModelMode, ModelModeConfig][]).map(
            ([key, cfg]) => (
              <label
                key={key}
                className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition ${
                  modelMode === key
                    ? "border-brand-accent bg-brand-accent/5"
                    : "border-gray-200 hover:border-gray-300"
                } ${!cfg.enabled ? "opacity-70" : ""}`}
              >
                <input
                  type="radio"
                  name="modelMode"
                  value={key}
                  checked={modelMode === key}
                  disabled={!cfg.enabled}
                  onChange={() => onModelModeChange(key)}
                  className="mt-1"
                />
                <span className="flex-1">
                  <span className="block text-sm font-semibold text-gray-900">
                    {cfg.label} — {cfg.displayModel}
                  </span>
                  {!cfg.enabled && (
                    <span className="mt-0.5 block text-xs text-amber-600">
                      Reserved for future implementation
                    </span>
                  )}
                  {cfg.warning && (
                    <span className="mt-1 block text-xs font-medium text-red-600">
                      {cfg.warning}
                    </span>
                  )}
                </span>
              </label>
            )
          )}
        </div>

        {fallbackNotice && (
          <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            {fallbackNotice}
          </div>
        )}

        <p className="mt-4 text-xs text-gray-400">
          Model selection is stored in your browser. Backend uses the currently connected
          deployment until additional models are enabled.
        </p>

        <div className="mt-5 border-t border-gray-200 pt-4">
          <p className="mb-3 text-sm font-medium text-gray-700">Account</p>

          {user ? (
            <>
              <div className="mb-3 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5">
                <p className="truncate text-sm font-semibold text-gray-900">
                  {user.display_name}
                </p>
                <p className="truncate text-xs text-gray-500">{user.username}</p>
                {user.role && user.role !== "user" && (
                  <span className="mt-1.5 inline-block rounded-full bg-brand-accent/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-brand-navy">
                    {user.role}
                  </span>
                )}
              </div>

              <button
                type="button"
                onClick={handleEnrollSecurityKey}
                disabled={enrollingKey}
                className="mb-2 w-full rounded-xl border border-green-200 bg-green-50 px-4 py-2.5 text-sm font-semibold text-green-700 transition hover:bg-green-100 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {enrollingKey ? "Preparing security key…" : "Add security key"}
              </button>

              {keyError && (
                <div className="mb-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
                  {keyError}
                </div>
              )}

              <button
                type="button"
                onClick={() => setShowPasswordChange(true)}
                className="mb-2 w-full rounded-xl border border-blue-200 bg-blue-50 px-4 py-2.5 text-sm font-semibold text-blue-700 transition hover:bg-blue-100"
              >
                Change password
              </button>

              {onOpenAdmin && (
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    onOpenAdmin();
                  }}
                  className="mb-2 w-full rounded-xl border border-brand-accent/30 bg-brand-accent/5 px-4 py-2.5 text-sm font-semibold text-brand-navy transition hover:bg-brand-accent/10"
                >
                  Manage users
                </button>
              )}

              <button
                type="button"
                onClick={handleSignOut}
                disabled={signingOut}
                className="w-full rounded-xl border border-rose-200 bg-rose-50 px-4 py-2.5 text-sm font-semibold text-rose-700 transition hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {signingOut ? "Signing out…" : "Sign out"}
              </button>
            </>
          ) : (
            <p className="text-xs text-gray-400">Not signed in.</p>
          )}
        </div>
      </div>

      {showPasswordChange && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            aria-label="Close password change"
            onClick={() => setShowPasswordChange(false)}
          />
          <div className="relative z-10 w-full max-w-sm rounded-2xl border border-gray-200 bg-white p-5 shadow-xl">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-bold text-brand-navy">Change Password</h3>
              <button
                type="button"
                onClick={() => setShowPasswordChange(false)}
                className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            {passwordError && (
              <div className="mb-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
                {passwordError}
              </div>
            )}

            <div className="mb-3">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                New password
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="At least 8 characters"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-accent focus:outline-none"
              />
            </div>

            <div className="mb-4">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Confirm password
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter password"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-accent focus:outline-none"
              />
            </div>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setShowPasswordChange(false)}
                className="flex-1 rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-700 transition hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleChangePassword}
                disabled={changingPassword}
                className="flex-1 rounded-lg bg-brand-accent px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {changingPassword ? "Changing…" : "Change password"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
